import { AfterViewInit, Directive, Host, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FilterState } from '@app/core/services/container/filter.service';
import { SidenavService } from '@app/core/services/container/sidenav.service';
import { FilterOutput } from '@app/shared/models/filter.interface';
import { Subject, Subscription } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { DataTableNewComponent } from '../data-table-new.component';
import { DataTableState } from '../state/state.model';
import { StateService } from '../state/state.service';

@Directive({ selector: '[vfsDatatableAdvancedFilter]', exportAs: 'vfsDatatableAdvancedFilter' })
export class DatatableAdvancedFilterDirective implements OnInit, AfterViewInit, OnDestroy {
  stateId: string;
  stateChangeSubject$: Subject<DataTableState>;

  filterButtonSubscription: Subscription;
  filterState: FilterOutput[];

  @Input() single: boolean;
  @Input() filterSubject$: Subject<FilterOutput[]>;

  constructor(
    @Host() private parent: DataTableNewComponent<any>,
    @Inject('StateService') private stateService: StateService,
    protected sidenavService: SidenavService
  ) {}

  ngOnInit() {
    this.stateId = this.parent.stateId;
    this.stateChangeSubject$ = this.parent.stateChangeSubject$;
    this.parent.withFilters = !this.single;
  }

  ngAfterViewInit() {
    const currentState: DataTableState = this.stateService.get(this.stateId);
    currentState.page.index = 0;

    this.hasInitialFilterActive(currentState.filter);

    if (!this.single) {
      this.filterButtonSubscription = this.parent.filtersButton.buttonClick
        .pipe(
          switchMap(_ => this.sidenavService.open(this.parent.filterBuilderDirective.parent)),
          tap(result => {
            if (result.meta && result.meta.accepted) {
              this.filterState = this.parent.filterBuilderDirective
                ? this.parent.filterBuilderDirective.buildFilter()
                : [];
            }

            if (!result.opened && result.meta && result.meta.accepted) {
              this.parent.areFiltersApplied = false;
              this.applyFilters(currentState);
            }
          })
        )
        .subscribe();
    } else {
      this.filterSubject$
        .pipe(
          tap(filters => {
            this.filterState = filters;
            this.applyFilters(currentState);
          })
        )
        .subscribe();
    }
  }

  applyFilters(currentState: DataTableState) {
    if (this.filterState.length > 0) {
      this.filterState.forEach(filter => {
        currentState.filter.value[filter.key] = filter;
      });
      this.hasFilterActive(this.filterState);
      this.stateService.update(currentState);

      this.stateChangeSubject$.next(currentState);
    }
  }

  hasFilterActive(filters: FilterOutput[]) {
    filters.forEach(filter => {
      if (this.valueIsNull(filter) && !filter.hidden) {
        this.parent.areFiltersApplied = true;
      }
    });
  }

  hasInitialFilterActive(filterState: FilterState) {
    const keys = Object.keys(filterState.value);
    this.parent.areFiltersApplied = keys
      .map(key => (filterState.value[key] ? filterState.value[key] : null))
      .filter(value => {
        return !value.hidden && value.value !== null;
      })
      .some(value => this.valueIsNull(value));
  }

  valueIsNull(value: FilterOutput): boolean {
    const filterValue = value.value;
    if (Array.isArray(filterValue)) {
      return (filterValue as []).length > 0;
    }
    return filterValue != null && filterValue !== '';
  }

  ngOnDestroy() {
    if (this.filterButtonSubscription) {
      this.filterButtonSubscription.unsubscribe();
    }
  }
}
